/*
 * Announces the server's refusal of a bundle too old to report every action
 * (`score_sync_require_current_client`). The refusal lands before any read or write, so buffered
 * scores are safe — but nothing on this page load will sync until a newer bundle is loaded.
 * One chokepoint, so every recordUserSession call site is covered. See
 * "`score_sync_require_current_client` — the old-bundle gate" in the server's
 * docs/score-sync-client-contract.md.
 */
import { onError } from '@apollo/client/link/error';

import { readClientUpdateRequired } from '../helpers/graphql-error.helpers';

type ClientUpdateListener = () => void;

const listeners = new Set<ClientUpdateListener>();
let announced = false;

// Returns the unsubscribe. A listener registered after the refusal is told immediately, so a
// component that mounts late still prompts.
export const subscribeClientUpdateRequired = (listener: ClientUpdateListener) => {
  listeners.add(listener);
  if (announced) listener();

  return () => {
    listeners.delete(listener);
  };
};

// Tests only: the module-level latch would otherwise leak between cases.
export const resetClientUpdateRequired = () => {
  listeners.clear();
  announced = false;
};

export const createClientUpdateLink = () => onError((errorResponse) => {
  if (announced || !readClientUpdateRequired(errorResponse)) return;

  // Every in-flight operation fails together when the switch is flipped; the prompt shows once.
  announced = true;
  listeners.forEach((listener) => {
    try {
      listener();
    } catch (err) {
      console.error('client update listener failed', err);
    }
  });
});
